/**
 * El asistente del alta — `OD-0039` (ACCEPTED 2026-09-27), con la enmienda
 * **E-13** (PROPUESTA 2026-10-20): de diálogo con una lista a **tres
 * pantallas seguidas a pantalla completa**.
 *
 * ```text
 * 1  DATOS FISCALES   razon social, NIF y domicilio, rellenados AQUI mismo
 * 2  APODERAMIENTO    "Empezar" -> pasos de la Sede -> "Ya lo he hecho"
 * 3  SEGUNDO FACTOR   el alta del TOTP, AQUI mismo (PantallaSeguridad en linea)
 *
 * LA PUERTA SE ABRE cuando estan 2 y 3, no antes (OD-0039 E-09). El 1 va
 * dentro del 2: sin obligado a expedir no hay a quien representar, y la API
 * rechaza autorizar (LEGAL_ENTITY_MISSING).
 * ```
 *
 * ## Por qué tres pantallas y en este orden
 *
 * El orden es el de las dependencias, que es también el que entiende quien lo
 * hace por primera vez: primero quién factura, luego el permiso que da ese
 * NIF, y al final el segundo factor, que no depende de nada. Hasta E-13 la
 * lista decía 1-autorización, 2-MFA, 3-datos, y el paso 3 había que hacerlo
 * el primero: el número contradecía el recorrido.
 *
 * Y una pantalla por paso, con la explicación al lado, porque cada uno pide
 * algo distinto —un formulario, un trámite fuera, una app en el móvil— y en
 * un diálogo de 560 px no cabía explicar ninguno. Los dos formularios que
 * antes estaban DETRÁS de la puerta (datos fiscales en Configuración, alta del
 * TOTP en Seguridad) ahora se rellenan dentro: salir a otra pantalla a mitad
 * del recorrido era la fricción que el asistente quita.
 *
 * Se retoma en el **primer paso sin hacer**. No se guarda en qué pantalla se
 * quedó: lo dice el estado real que devuelve la API, que es el único que vale.
 *
 * ## Lo que NO cambia de E-11
 *
 * ```text
 * SE CIERRA    aspa, "Seguir mas tarde" y Escape. A pantalla completa no hay
 *              "fuera" donde pinchar
 * CERRAR NO    completa nada: solo oculta. No llama a la API ni se guarda
 * DONDE SE     en el control previo de la emision, en el SERVIDOR
 * EXIGE        (evaluateIssuancePreflight). Ahi no se puede cerrar
 * ```
 *
 * Este asistente informa y acompaña; la regla la aplica la API, que no atiende
 * a ningún estado de interfaz.
 *
 * ## «Hecho» quiere decir que ya no te impide facturar (`E-14`, PROPUESTA)
 *
 * La auditoría del 2026-10-29 encontró dos «Hecho» que no lo eran: el paso 1
 * con los datos guardados SIN CONFIRMAR, y el 2 con la autorización todavía
 * en comprobación. Y la pregunta del SII, que puede dejar a la persona fuera,
 * vivía sólo en Configuración.
 *
 * ```text
 * PASO 1   HECHO      guardado + SII respondido "No" + CONFIRMADO
 *          ESPERA     guardado sin confirmar ("Guardado, falta confirmar")
 *          SII "Si"   pantalla de salida: Faktelio no emite sus facturas
 * PASO 2   HECHO      ACTIVE
 *          ESPERA     PENDING_VALIDATION ("En comprobacion"): no bloquea
 *                     seguir, pero no es "Hecho"
 * CONFIRMAR           casilla expresa + boton, DENTRO del asistente: en el
 *                     paso 1 si el segundo factor ya esta activo, y si no al
 *                     final, porque confirmar exige MFA (OD-0028 D-07)
 * ```
 *
 * Confirmar sigue siendo un acto aparte de guardar (`OD-0015` D-03): una
 * casilla que la persona marca, con el aviso de que los datos se congelan.
 * Lo que cambia es el sitio, no el acto.
 *
 * ## Accesibilidad (`OD-0017` D-05)
 *
 * Sigue siendo un modal —tapa el panel entero—, así que `role="dialog"`,
 * `aria-modal`, foco al título al abrirse y al cambiar de paso, y **foco
 * atrapado dentro mientras está abierto**: sin eso, quien navega con teclado
 * se va detrás y no vuelve.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ESTADO_VALIDACION, type Autorizacion, type DatosEmisor, type Emisor, type Resultado } from "../api/cliente";
import { t } from "../i18n/es";
import { estadoAutorizacion, estadoFiscal, estadoMfa, type EstadoPasoAlta } from "../ui/pasos-del-alta";
import { conCodigoPostal } from "../ui/provincia-por-cp";
import { Aviso, Boton, Campo, Casilla, Selector } from "../ui/primitivas";
import { datosDe, SII, TIPOS, VACIO } from "./PantallaEmisor";
import { PantallaSeguridad } from "./PantallaSeguridad";

interface Peticion {
  (): Promise<Resultado<Autorizacion>>;
}

/**
 * Estados en los que la persona todavía tiene que hacer algo.
 *
 * `PENDING_SIGNATURE` está aquí, y es la diferencia entre una puerta y un
 * pestillo: ese estado significa «ha elegido la ruta», no «ha autorizado».
 * Dejarlo fuera tenía dos efectos, los dos malos: cualquiera abría la puerta
 * pulsando «Empezar» sin pisar la Sede, y los pasos del trámite —que viven en
 * este mismo asistente— no los veía nadie. La puerta se abre con «Ya lo he
 * hecho», no con «Empezar».
 */
const BLOQUEAN: readonly string[] = ["NOT_STARTED", "PENDING_SIGNATURE", "REJECTED", "REVOKED", "NIF_CHANGED"];

const FOCUSABLES = 'a[href], button:not([disabled]), input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';

type Paso = 1 | 2 | 3;
const PASOS: readonly Paso[] = [1, 2, 3];
const NOMBRE_PASO: Readonly<Record<Paso, string>> = {
  1: t.puerta.nombrePasoDatos,
  2: t.puerta.nombrePasoAutorizacion,
  3: t.puerta.nombrePasoMfa,
};

/**
 * Cómo está cada paso, que es lo que se ve en el indicador (`E-14`).
 *
 * `espera` es lo que no es ni lo uno ni lo otro: lo has hecho tú, pero todavía
 * no te deja facturar (datos sin confirmar, autorización en comprobación). Se
 * pinta en ámbar y con su texto, nunca con el ✓ de «Hecho».
 */
type EstadoDelPaso = "hecho" | "espera" | "pendiente";

/**
 * El estado de LA lista (`ui/pasos-del-alta`), tal como lo pinta el indicador:
 * el asistente no calcula el suyo, para que diga lo mismo que «Tareas
 * pendientes» y «Primeros pasos» (hallazgo 4 de la auditoría). Lo que no se
 * sabe todavía se pinta como pendiente: no se da nada por hecho.
 */
function enIndicador(estado: EstadoPasoAlta): EstadoDelPaso {
  return estado === "hecho" ? "hecho" : estado === "espera" ? "espera" : "pendiente";
}

/** Lo que se ve debajo del indicador: un paso, confirmar, la salida del SII o el final. */
type Vista = Paso | "confirmar" | "sii" | "listo";

/** Los campos del paso 1 que pide la Sede: quién y dónde. Lo demás, luego. */
type CampoFiscal = "legalName" | "taxIdValue" | "addressLine" | "postalCode" | "city" | "province";

export function PuertaAutorizacion({
  hayConexion,
  onSesionCaducada,
  onCerrar,
  onCambio,
  onIrADatosFiscales,
}: {
  hayConexion: boolean;
  onSesionCaducada: () => void;
  /**
   * Ocultar el asistente. ESTADO DE INTERFAZ: no completa nada, no guarda nada
   * y no se consulta para decidir si se puede emitir (`OD-0039` E-11).
   */
  onCerrar: () => void;
  /** Para que el resto del panel refleje el estado nuevo. */
  onCambio: () => void;
  /**
   * La pantalla ENTERA de datos fiscales: lo opcional del paso 1 y la
   * confirmación del final. Cierra el asistente; no es parte del recorrido.
   */
  onIrADatosFiscales: () => void;
}) {
  const [datos, setDatos] = useState<Autorizacion | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  /**
   * Si acaba de elegir la ruta, los pasos de la Sede se ven abiertos. Si llega
   * con el trámite ya empezado es que VUELVE de la Sede, y entonces lo primero
   * es la pregunta, no releer cuatro pasos que ya ha seguido (OD-0039 D-03).
   */
  const [acabaDeElegir, setAcabaDeElegir] = useState(false);
  const [copiado, setCopiado] = useState(false);
  /** Lo dice la API (`issuerReady`); el clic sólo puede confirmarlo. */
  const [faltanDatosFiscales, setFaltanDatosFiscales] = useState(false);
  /**
   * El segundo factor. `null` = todavía no se sabe, y entonces la puerta NO se
   * abre: no saber si está activo no es lo mismo que saber que lo está.
   */
  const [mfaActivo, setMfaActivo] = useState<boolean | null>(null);
  /** El emisor tal como está en la API: base del paso 1 y aviso del final. */
  const [emisor, setEmisor] = useState<Emisor | null>(null);
  const [fiscal, setFiscal] = useState<DatosEmisor>(VACIO);
  const [erroresFiscal, setErroresFiscal] = useState<Readonly<Record<string, string>>>({});
  /** Lo que se rechazó (hallazgo 13): editar un campo retira su error. */
  const [fiscalDelError, setFiscalDelError] = useState<DatosEmisor | null>(null);
  /**
   * La casilla de «He revisado que los datos son correctos» (`OD-0015` D-03).
   * Es la confirmación EXPRESA: sin marcarla no se llama a confirmar.
   */
  const [revisado, setRevisado] = useState(false);
  const [errorRevisado, setErrorRevisado] = useState<string | null>(null);
  /**
   * El paso que se ve. `null` = el primero sin hacer, que es por donde se
   * retoma. Se fija sólo cuando la persona elige otro.
   */
  const [pasoElegido, setPasoElegido] = useState<Paso | null>(null);
  /**
   * Si en esta visita llegó a faltar algo. Es lo que distingue «acaba de
   * terminar» —y merece la pantalla final— de «ya estaba todo hecho al
   * entrar», que no enseña nada.
   */
  const [habiaPendiente, setHabiaPendiente] = useState(false);
  const [destino, setDestino] = useState<"YO" | "GESTORIA">("GESTORIA");
  const [correo, setCorreo] = useState("");
  const [avisoCorreo, setAvisoCorreo] = useState<string | null>(null);
  const dialogo = useRef<HTMLDivElement | null>(null);
  const titulo = useRef<HTMLHeadingElement | null>(null);
  const cuerpo = useRef<HTMLDivElement | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    // El segundo factor se pregunta SIEMPRE: es el otro paso de la puerta.
    const [r, mfa, e] = await Promise.all([api.autorizacionAeat(), api.estadoMfa(), api.obtenerEmisor()]);
    if (mfa.ok) setMfaActivo(mfa.datos.totp === "ACTIVE");
    if (e.ok) setEmisor(e.datos.emisor);
    if (r.ok) {
      setDatos(r.datos);
      setFaltanDatosFiscales(!r.datos.datosFiscalesListos);
    }
    else if (r.estado === 401) onSesionCaducada();
    // Un fallo de carga NO levanta la puerta: sin saber el estado, se sigue
    // pidiendo la autorización. Fallar abierto aquí sería dejar entrar a
    // quien no ha autorizado nada.
  }, [onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // El formulario del paso 1 parte de lo que ya hay. Sólo cuando cambia el
  // emisor guardado: no se pisa lo que la persona está escribiendo.
  useEffect(() => {
    setFiscal(emisor === null ? VACIO : datosDe(emisor));
  }, [emisor]);

  /**
   * Al volver a esta pestaña se vuelve a preguntar el estado. El trámite se
   * hace FUERA —en la Sede, o en la guía de `/como-autorizar`, que también
   * tiene el botón de "ya lo he hecho"—, así que quien vuelve suele volver
   * con algo ya hecho, y encontrarse la puerta igual que la dejó es lo que
   * hace pulsar dos veces y comerse un error.
   */
  useEffect(() => {
    const alVolver = (): void => {
      if (document.visibilityState === "visible") void cargar();
    };
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("focus", alVolver);
    return () => {
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("focus", alVolver);
    };
  }, [cargar]);

  /**
   * Los DOS pasos que abren la puerta. Falta cualquiera, sigue puesta
   * (`OD-0039` E-09).
   *
   * `mfaActivo === null` cuenta como "falta": no saber si está activo no es
   * saber que lo está, y aquí se falla cerrado como en todo lo demás.
   */
  const faltaAutorizacion = datos !== null && BLOQUEAN.includes(datos.estado);
  const faltaMfa = mfaActivo !== true;
  /**
   * `E-14`: quien declara que lleva el SII no puede emitir con Faktelio
   * (`OD-0035`). Para esa persona el asistente no tiene nada que pedir: ni la
   * autorización ni el segundo factor le van a dejar facturar.
   */
  const enSii = emisor?.siiStatus === "SII";
  /**
   * Los datos fiscales SÓLO están si además están confirmados y el SII
   * respondido: es lo que mira el control previo de la emisión. Guardados sin
   * más, la primera factura se para (`E-14`, hallazgo 1 de la auditoría).
   */
  const fiscalCompleto = emisor !== null && emisor.confirmedAt !== null && emisor.siiStatus === "NOT_SII";
  /**
   * Si queda algo por hacer. Decide SI SE VE el asistente, no si se puede
   * emitir: eso lo decide el control previo del servidor, que mira lo mismo y
   * algunas cosas más (cliente, líneas, cuota, declaración responsable).
   */
  const faltaAlgo = datos !== null && !enSii && (faltaAutorizacion || faltaMfa || !fiscalCompleto);

  /**
   * Lo que ve la persona en el indicador. «Hecho» sólo cuando ese paso ya no
   * le impide facturar; lo que está hecho por su parte y aún no vale, en
   * espera (`E-14`).
   */
  const estadoDe: Readonly<Record<Paso, EstadoDelPaso>> = {
    1: enIndicador(
      estadoFiscal(
        emisor === null || faltanDatosFiscales ? "sin-datos" : emisor.confirmedAt === null ? "sin-confirmar" : "confirmado",
        emisor?.siiStatus ?? null,
      ),
    ),
    2: enIndicador(estadoAutorizacion(datos?.estado ?? null)),
    3: enIndicador(estadoMfa(mfaActivo)),
  };
  /**
   * Lo que hace falta para PASAR de cada paso, que no es lo mismo que «Hecho».
   * El paso 1 se deja atrás con los datos guardados y el SII respondido; si
   * quedan sin confirmar, se confirman al final, en su pantalla, que es donde
   * se retoma con los otros dos pasados. El 2 se deja atrás en comprobación.
   */
  const paraSeguir: Readonly<Record<Paso, boolean>> = {
    1: !faltanDatosFiscales && emisor?.siiStatus === "NOT_SII",
    2: !faltaAutorizacion,
    3: !faltaMfa,
  };
  const primerPendiente: Paso | undefined = PASOS.find((p) => !paraSeguir[p]);
  const paso: Paso = pasoElegido ?? primerPendiente ?? 1;
  /** Sin datos fiscales sólo se puede estar en el 1: los otros dos dependen de él. */
  const alcanzable = (p: Paso): boolean => p === 1 || !faltanDatosFiscales;

  useEffect(() => {
    if (faltaAlgo) setHabiaPendiente(true);
  }, [faltaAlgo]);

  const visible = faltaAlgo || (habiaPendiente && datos !== null);
  /**
   * Qué se enseña. Lo elegido por la persona manda; si no ha elegido nada, el
   * primer paso sin pasar, y con los tres pasados, confirmar lo que falte.
   */
  const vista: Vista = enSii
    ? "sii"
    : !faltaAlgo
      ? "listo"
      : pasoElegido !== null
        ? pasoElegido
        : primerPendiente ?? "confirmar";
  const terminado = visible && (vista === "listo" || vista === "sii");

  /*
    El foco entra al abrirse y no sale MIENTRAS ESTA ABIERTO: Tab y Shift+Tab
    dan la vuelta dentro. Escape lo cierra, como cualquier modal.
  */
  useEffect(() => {
    if (!visible) return;
    const alPulsar = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCerrar();
        return;
      }
      if (e.key !== "Tab" || dialogo.current === null) return;
      const foco = [...dialogo.current.querySelectorAll<HTMLElement>(FOCUSABLES)].filter((x) => x.offsetParent !== null);
      if (foco.length === 0) return;
      const primero = foco[0]!;
      const ultimo = foco[foco.length - 1]!;
      const activo = document.activeElement;
      if (e.shiftKey && (activo === primero || activo === titulo.current)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && activo === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener("keydown", alPulsar, true);
    return () => document.removeEventListener("keydown", alPulsar, true);
  }, [visible, onCerrar]);

  // Cada pantalla nueva empieza arriba y con el foco en su título: es lo que
  // anuncia el lector de pantalla, y lo que se lee primero con los ojos.
  useEffect(() => {
    if (!visible) return;
    cuerpo.current?.scrollTo({ top: 0 });
    titulo.current?.focus();
  }, [visible, vista]);

  if (!visible || datos === null) return null;

  const irAPaso = (p: Paso): void => {
    setMensaje(null);
    setPasoElegido(p);
  };

  /**
   * "Continuar": al siguiente que falte; con los tres pasados, a lo que quede
   * (confirmar), y si no falta ninguno, al siguiente.
   */
  const siguiente = (): void => {
    const despues = PASOS.filter((p) => p > paso);
    const falta = despues.find((p) => !paraSeguir[p]);
    if (falta === undefined && primerPendiente === undefined) {
      setMensaje(null);
      setPasoElegido(null);
      return;
    }
    irAPaso(falta ?? despues[0] ?? paso);
  };

  async function accion(hacer: Peticion): Promise<void> {
    setMensaje(null);
    setOcupado(true);
    const r = await hacer();
    setOcupado(false);
    if (r.ok) {
      setDatos(r.datos);
      onCambio();
      return;
    }
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setMensaje(t.autorizacion.soloPropietario);
    // Si los datos fiscales se han borrado entre la carga y el clic, se vuelve
    // al paso 1 en vez de enseñar un error que no dice qué hacer.
    if (r.error?.motivo === "LEGAL_ENTITY_MISSING") {
      setFaltanDatosFiscales(true);
      return setPasoElegido(1);
    }
    setMensaje(r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico);
  }

  /**
   * Confirma los datos guardados (`OD-0015` D-03). SÓLO con la casilla
   * marcada: la casilla es la confirmación expresa, y el botón sin ella no
   * llama a nada. Exige el segundo factor en la API (`OD-0028` D-07), y por
   * eso sólo se ofrece cuando está activo.
   */
  async function confirmarFiscal(): Promise<boolean> {
    if (!revisado) {
      setErrorRevisado(t.emisor.faltaCasilla);
      return false;
    }
    setErrorRevisado(null);
    setOcupado(true);
    const r = await api.confirmarEmisor();
    setOcupado(false);
    if (r.ok) {
      setEmisor(r.datos.emisor);
      setRevisado(false);
      onCambio();
      return true;
    }
    if (r.estado === 401) {
      onSesionCaducada();
      return false;
    }
    if (r.estado === 403) {
      setMensaje(r.error?.motivo === "MFA_REQUIRED" ? t.puerta.confirmarSinMfa : t.emisor.soloOwner);
      return false;
    }
    setMensaje(r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico);
    return false;
  }

  /**
   * Guarda SÓLO lo que se ve en el paso 1, sobre lo que ya hubiera: un emisor
   * con IBAN o pie de factura no los pierde por pasar por aquí.
   *
   * Con el segundo factor activo, la casilla de «He revisado…» va en este
   * mismo paso y es obligatoria para seguir: guardar y confirmar son dos
   * llamadas y dos actos —la casilla es el segundo—, pero en una pantalla
   * (`E-14`). Sin segundo factor la confirmación espera al final.
   */
  async function guardarFiscal(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setMensaje(null);
    setErroresFiscal({});
    // Sin responder no se emite (OD-0035): aquí se pregunta, no se presupone.
    if (fiscal.siiStatus === null) {
      setFiscalDelError(fiscal);
      setErroresFiscal({ siiStatus: "SII_REQUIRED" });
      return;
    }
    const pideCasilla = !faltaMfa && fiscal.siiStatus !== "SII" && emisor?.confirmedAt == null;
    if (pideCasilla && !revisado) {
      setErrorRevisado(t.emisor.faltaCasilla);
      return;
    }
    setOcupado(true);
    const base = emisor === null ? VACIO : datosDe(emisor);
    const r = await api.guardarEmisor({
      ...base,
      legalName: fiscal.legalName,
      taxIdType: fiscal.taxIdType,
      taxIdValue: fiscal.taxIdValue,
      addressLine: fiscal.addressLine,
      postalCode: fiscal.postalCode,
      city: fiscal.city,
      province: fiscal.province,
      siiStatus: fiscal.siiStatus,
    });
    setOcupado(false);
    if (r.ok) {
      setEmisor(r.datos.emisor);
      onCambio();
      // Guardar lo mismo que ya estaba confirmado no borra la confirmación
      // (R-E2): entonces no hay nada que volver a confirmar.
      if (pideCasilla && r.datos.emisor !== null && r.datos.emisor.confirmedAt === null) {
        if (!(await confirmarFiscal())) return;
      }
      // La API es quien dice si ya hay a quién representar: se vuelve a
      // preguntar en vez de suponerlo, y entonces se avanza al primer paso
      // que falte.
      await cargar();
      setPasoElegido(null);
      return;
    }
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setMensaje(t.emisor.soloOwner);
    if (r.estado === ESTADO_VALIDACION && r.error !== null) {
      setFiscalDelError(fiscal);
      return setErroresFiscal(r.error.campos);
    }
    if (r.error?.motivo === "TAX_ID_LOCKED") return setMensaje(t.emisor.nifBloqueado);
    if (r.error?.motivo === "SII_EXIT_REQUIRES_SUPPORT") return setMensaje(t.emisor.siiSalida);
    setMensaje(r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico);
  }

  /** Al portapapeles, con lo que hay que pegar en el formulario de la Sede. */
  async function copiarApoderado(): Promise<void> {
    try {
      await navigator.clipboard.writeText(apoderadoParaCopiar);
      setCopiado(true);
    } catch {
      // Sin permiso de portapapeles no se rompe nada: los datos estan a la
      // vista y se pueden seleccionar a mano.
      setCopiado(false);
    }
  }

  async function enviarPasos(): Promise<void> {
    setAvisoCorreo(null);
    setOcupado(true);
    const r = await api.enviarInstruccionesAutorizacion(destino, correo.trim());
    setOcupado(false);
    if (r.ok) {
      setAvisoCorreo(r.datos.enviado ? t.puerta.enviado : t.puerta.yaEnviadoHoy);
      return;
    }
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setAvisoCorreo(t.autorizacion.soloPropietario);
    setAvisoCorreo(r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico);
  }

  const estado = datos.estado;
  const apoderado = `${datos.apoderado.nombre} (NIF ${datos.apoderado.nif})`;
  const apoderadoParaCopiar = `${datos.apoderado.nombre} ${datos.apoderado.nif}`;
  const empezando = estado === "NOT_STARTED" || estado === "REJECTED" || estado === "REVOKED" || estado === "NIF_CHANGED";
  const nifBloqueado = emisor?.taxIdLocked === true;
  const cambiarFiscal = (campo: CampoFiscal) => (valor: string) =>
    setFiscal((d) => (campo === "postalCode" ? conCodigoPostal(d, valor) : { ...d, [campo]: valor }));
  const errorFiscal = (campo: string): string | null => {
    if (erroresFiscal[campo] === undefined) return null;
    if (fiscalDelError !== null && campo in fiscal && fiscal[campo as keyof DatosEmisor] !== fiscalDelError[campo as keyof DatosEmisor]) return null;
    return t.emisor.errores[erroresFiscal[campo]] ?? t.comun.errorGenerico;
  };

  /** El texto de debajo de cada paso: siempre escrito, nunca sólo el color. */
  const estadoPaso = (p: Paso): string => {
    if (estadoDe[p] === "hecho") return t.puerta.hecho;
    if (p === 1 && enSii) return t.puerta.conSii;
    if (p === 1 && estadoDe[1] === "espera") return emisor?.siiStatus === null ? t.puerta.faltaSii : t.puerta.faltaConfirmar;
    if (p === 2 && estadoDe[2] === "espera") return t.puerta.enComprobacion;
    if (p === 2 && estado === "PENDING_SIGNATURE") return t.puerta.enCurso;
    return t.puerta.pendiente;
  };
  /** Lo que va en el círculo: ✓ hecho, ◷ en espera, el número si no. */
  const marcaPaso = (p: Paso): string => (estadoDe[p] === "hecho" ? "✓" : estadoDe[p] === "espera" ? "◷" : String(p));
  /** El paso 1 pide la casilla si confirmar ya es posible: con el segundo factor activo. */
  const casillaEnPaso1 = !faltaMfa && fiscal.siiStatus !== "SII" && emisor?.confirmedAt == null;

  return (
    <div className="asistente" role="dialog" aria-modal="true" aria-labelledby="puerta-titulo" ref={dialogo}>
      <header className="asistente-cabecera">
        <span className="marca asistente-marca">{t.app.marca}</span>
        {/*
          Los TRES pasos, siempre a la vista y en orden. Que se vea lo que
          falta y lo que ya esta hecho es lo que convierte un muro en un
          tramite: sin esto, quien termina el primero no sabe que le quedan
          otros. Cada uno se pulsa para volver a el, salvo los que dependen
          de los datos fiscales mientras falten.
        */}
        <ol className="asistente-pasos" aria-label={t.puerta.titulo}>
          {PASOS.map((p) => (
            <li key={p} data-hecho={estadoDe[p] === "hecho"} data-espera={estadoDe[p] === "espera"} data-actual={vista === p}>
              <button
                type="button"
                onClick={() => irAPaso(p)}
                disabled={!alcanzable(p) || enSii}
                aria-current={vista === p ? "step" : undefined}
              >
                <span className="asistente-numero" aria-hidden="true">
                  {marcaPaso(p)}
                </span>
                <span className="asistente-paso-texto">
                  <span className="asistente-paso-nombre">{NOMBRE_PASO[p]}</span>
                  <span className="asistente-paso-estado">{estadoPaso(p)}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
        {/*
          La salida. Va en la cabecera, antes que el tramite: quien llega con
          teclado se encuentra la salida antes que el formulario, no despues
          de recorrerlo entero. Con texto en pantallas anchas y aspa en el
          movil, siempre con el mismo nombre accesible.
        */}
        <button type="button" className="puerta-cerrar" onClick={onCerrar} aria-label={t.puerta.cerrar}>
          <span className="asistente-salir-texto" aria-hidden="true">
            {t.puerta.seguirLuego}
          </span>
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div className="asistente-cuerpo" ref={cuerpo}>
        {vista === "sii" ? (
          /*
            E-14: la salida clara de quien lleva el SII. No es un error ni un
            muro: es decirle pronto, antes de apoderar a nadie, que Faktelio no
            puede emitir sus facturas (OD-0035), y que lo demas si funciona.
          */
          <div className="asistente-rejilla asistente-final">
            <section className="asistente-principal">
              <p className="puerta-etiqueta">{t.puerta.etiqueta}</p>
              <h2 id="puerta-titulo" tabIndex={-1} ref={titulo}>
                {t.puerta.siiTitulo}
              </h2>
              <p className="asistente-entradilla">{t.puerta.siiDetalle}</p>
              <ul>
                {t.puerta.siiSigue.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
              <Aviso tono="info">{t.emisor.siiSalida}</Aviso>
              <p>
                <a href={t.emisor.enlaceSii} target="_blank" rel="noopener noreferrer">
                  {t.emisor.enlaceSiiTexto}
                </a>
              </p>
              <div className="acciones">
                <Boton onClick={onCerrar}>{t.puerta.listoIrAlPanel}</Boton>
              </div>
            </section>
          </div>
        ) : vista === "listo" ? (
          <div className="asistente-rejilla asistente-final">
            <section className="asistente-principal">
              <p className="puerta-etiqueta">{t.puerta.etiqueta}</p>
              <h2 id="puerta-titulo" tabIndex={-1} ref={titulo}>
                {t.puerta.listoTitulo}
              </h2>
              <p className="asistente-entradilla">{t.puerta.listoDetalle}</p>
              {estado === "ACTIVE" ? (
                <Aviso tono="exito" titulo={t.autorizacion.activaTitulo}>
                  {t.autorizacion.activa(new Date(datos.validadaEl ?? Date.now()).toLocaleDateString("es-ES"))}
                </Aviso>
              ) : (
                <Aviso tono="info" titulo={t.autorizacion.comprobandoTitulo}>
                  {t.autorizacion.comprobando}
                </Aviso>
              )}
              <div className="acciones">
                <Boton onClick={onCerrar}>{t.puerta.listoIrAlPanel}</Boton>
              </div>
            </section>
          </div>
        ) : vista === "confirmar" ? (
          /*
            Emitir exige el emisor CONFIRMADO (OD-0015 D-03), y confirmar exige
            el segundo factor: por eso, a quien no lo tenia en el paso 1, se le
            pide aqui, AL FINAL y DENTRO, con su casilla. Antes este bloque
            mandaba a Configuracion y el asistente ya decia "Todo listo".
          */
          <div className="asistente-rejilla asistente-final">
            <section className="asistente-principal">
              <p className="puerta-etiqueta">
                {t.puerta.etiqueta} · {t.puerta.nombrePasoDatos}
              </p>
              <h2 id="puerta-titulo" tabIndex={-1} ref={titulo}>
                {t.puerta.confirmarTitulo}
              </h2>
              <p className="asistente-entradilla">{t.puerta.confirmarDetalle}</p>
              {emisor !== null ? (
                <dl className="asistente-resumen">
                  <div>
                    <dt>{t.emisor.legalName}</dt>
                    <dd>{emisor.legalName}</dd>
                  </div>
                  <div>
                    <dt>{t.emisor.taxIdValue}</dt>
                    <dd>{emisor.taxIdValue}</dd>
                  </div>
                  <div>
                    <dt>{t.emisor.bloqueDomicilio}</dt>
                    <dd>
                      {emisor.addressLine}, {emisor.postalCode} {emisor.city} ({emisor.province})
                    </dd>
                  </div>
                </dl>
              ) : null}
              <Aviso tono="aviso">{t.emisor.confirmarDetalle}</Aviso>
              <Casilla
                id="asistente-revisado-final"
                etiqueta={t.emisor.casilla}
                marcada={revisado}
                onChange={(v) => {
                  setRevisado(v);
                  if (v) setErrorRevisado(null);
                }}
                error={errorRevisado}
              />
              {mensaje !== null ? <Aviso tono="error">{mensaje}</Aviso> : null}
              {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
              <div className="asistente-navegacion">
                <Boton variante="secundario" onClick={() => irAPaso(1)}>
                  {t.puerta.confirmarCorregir}
                </Boton>
                <Boton onClick={() => void confirmarFiscal()} deshabilitado={ocupado || !hayConexion}>
                  {ocupado ? t.emisor.confirmando : t.emisor.confirmar}
                </Boton>
              </div>
            </section>
          </div>
        ) : (
          <div className="asistente-rejilla">
            <section className="asistente-principal">
              <p className="puerta-etiqueta">
                {t.puerta.pasoDeTotal(paso, PASOS.length)} · {NOMBRE_PASO[paso]}
              </p>

              {paso === 1 ? (
                <>
                  <h2 id="puerta-titulo" tabIndex={-1} ref={titulo}>
                    {t.puerta.fiscalTitulo}
                  </h2>
                  <p className="asistente-entradilla">
                    {t.puerta.fiscalEntradilla} <strong>{t.puerta.tarda(t.puerta.fiscalTarda)}</strong>
                  </p>
                  <p className="ayuda-neutra">{t.puerta.antesUnPresupuesto}</p>
                  <form className="asistente-formulario" onSubmit={guardarFiscal} noValidate>
                    <h3>{t.emisor.bloqueIdentidad}</h3>
                    <Campo
                      id="asistente-nombre"
                      etiqueta={t.emisor.legalName}
                      valor={fiscal.legalName}
                      onChange={cambiarFiscal("legalName")}
                      autoComplete="organization"
                      error={errorFiscal("legalName")}
                    />
                    <div className="fila-campos">
                      <Selector
                        id="asistente-tipo"
                        etiqueta={t.emisor.taxIdType}
                        valor={fiscal.taxIdType}
                        opciones={TIPOS}
                        onChange={(v) => setFiscal((d) => ({ ...d, taxIdType: v }))}
                        error={errorFiscal("taxIdType")}
                        deshabilitado={nifBloqueado}
                      />
                      <Campo
                        id="asistente-nif"
                        etiqueta={t.emisor.taxIdValue}
                        valor={fiscal.taxIdValue}
                        onChange={cambiarFiscal("taxIdValue")}
                        autoComplete="off"
                        error={errorFiscal("taxIdValue")}
                        soloLectura={nifBloqueado}
                        ayuda={nifBloqueado ? t.emisor.nifBloqueado : null}
                      />
                    </div>
                    <h3>{t.emisor.bloqueDomicilio}</h3>
                    <Campo
                      id="asistente-direccion"
                      etiqueta={t.emisor.addressLine}
                      valor={fiscal.addressLine}
                      onChange={cambiarFiscal("addressLine")}
                      autoComplete="street-address"
                      error={errorFiscal("addressLine")}
                    />
                    <div className="fila-campos">
                      <Campo
                        id="asistente-cp"
                        etiqueta={t.emisor.postalCode}
                        valor={fiscal.postalCode}
                        onChange={cambiarFiscal("postalCode")}
                        inputMode="numeric"
                        autoComplete="postal-code"
                        error={errorFiscal("postalCode")}
                      />
                      <Campo
                        id="asistente-municipio"
                        etiqueta={t.emisor.city}
                        valor={fiscal.city}
                        onChange={cambiarFiscal("city")}
                        autoComplete="address-level2"
                        error={errorFiscal("city")}
                      />
                      <Campo
                        id="asistente-provincia"
                        etiqueta={t.emisor.province}
                        valor={fiscal.province}
                        onChange={cambiarFiscal("province")}
                        autoComplete="address-level1"
                        error={errorFiscal("province")}
                      />
                    </div>
                    {/*
                      E-14: el SII se pregunta AQUI, antes de apoderar a nadie.
                      Si la respuesta es "Si", Faktelio no puede emitir sus
                      facturas (OD-0035), y enterarse despues de dos pasos y
                      un tramite en la Sede es tarde. Con "Si" ya guardado no se
                      cambia desde aqui: solo por soporte.
                    */}
                    <h3>{t.puerta.siiBloque}</h3>
                    <Selector
                      id="asistente-sii"
                      etiqueta={t.emisor.siiPregunta}
                      valor={fiscal.siiStatus ?? ""}
                      opciones={SII}
                      onChange={(v) => setFiscal((d) => ({ ...d, siiStatus: v === "" ? null : v }))}
                      error={errorFiscal("siiStatus")}
                      deshabilitado={emisor?.siiStatus === "SII"}
                    />
                    <p className="ayuda-neutra">
                      {t.emisor.ayudaSii}{" "}
                      <a href={t.emisor.enlaceSii} target="_blank" rel="noopener noreferrer">
                        {t.emisor.enlaceSiiTexto}
                      </a>
                    </p>
                    {fiscal.siiStatus === "SII" ? (
                      <Aviso tono="aviso" titulo={t.puerta.siiAvisoTitulo}>
                        {t.puerta.siiAviso}
                      </Aviso>
                    ) : null}
                    {/*
                      La confirmacion expresa (OD-0015 D-03), en el mismo paso
                      cuando se puede: confirmar exige el segundo factor. Sin
                      el, se dice que se confirmara al final, para que nadie
                      crea que con guardar ya esta.
                    */}
                    {casillaEnPaso1 ? (
                      <>
                        <Aviso tono="aviso">{t.emisor.confirmarDetalle}</Aviso>
                        <Casilla
                          id="asistente-revisado"
                          etiqueta={t.emisor.casilla}
                          marcada={revisado}
                          onChange={(v) => {
                            setRevisado(v);
                            if (v) setErrorRevisado(null);
                          }}
                          error={errorRevisado}
                        />
                      </>
                    ) : fiscal.siiStatus !== "SII" && emisor?.confirmedAt == null ? (
                      <p className="ayuda-neutra">{t.puerta.confirmarAlFinal}</p>
                    ) : null}
                    {mensaje !== null ? <Aviso tono="error">{mensaje}</Aviso> : null}
                    {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
                    <div className="asistente-navegacion">
                      <Boton tipo="submit" deshabilitado={ocupado || !hayConexion}>
                        {ocupado ? t.puerta.fiscalGuardando : t.puerta.fiscalGuardar}
                      </Boton>
                    </div>
                  </form>
                  <p className="ayuda-neutra">
                    {t.puerta.fiscalResto}{" "}
                    <button type="button" className="enlace-en-texto" onClick={onIrADatosFiscales}>
                      {t.puerta.fiscalVerTodo}
                    </button>
                  </p>
                </>
              ) : null}

              {paso === 2 ? (
                <>
                  <h2 id="puerta-titulo" tabIndex={-1} ref={titulo}>
                    {t.puerta.autorizacionTitulo}
                  </h2>
                  <p className="asistente-entradilla">
                    {t.puerta.entradillaAntes}
                    <strong>{t.puerta.entradillaGratis}</strong>
                    {t.puerta.entradillaDespues}
                  </p>

                  {estado === "REJECTED" ? (
                    <Aviso tono="aviso" titulo={t.puerta.rechazadaTitulo}>
                      {t.autorizacion.motivos[datos.motivoRechazo ?? "OTHER"] ?? t.autorizacion.motivos.OTHER}
                    </Aviso>
                  ) : null}
                  {estado === "REVOKED" ? <Aviso tono="aviso">{t.puerta.revocada}</Aviso> : null}
                  {estado === "NIF_CHANGED" ? <Aviso tono="aviso">{t.puerta.nifCambiado}</Aviso> : null}
                  {mensaje !== null ? <Aviso tono="error">{mensaje}</Aviso> : null}

                  {empezando ? (
                    <>
                      <p className="ayuda-neutra">{t.puerta.porQue}</p>
                      {/*
                        Las dos formas, con el mismo peso visual (OD-0031 D-01).
                        La del certificado propio se VE y no se puede elegir:
                        OD-0039 D-04 explica por que, y ensenarla desactivada es
                        mas honesto que hacer como que no existe.
                      */}
                      <div className="opciones-autorizacion">
                        <div className="opcion-autorizacion opcion-recomendada">
                          <h3>{t.puerta.opcionSedeTitulo}</h3>
                          <p className="etiqueta-precio">
                            {t.autorizacion.gratis} · {t.puerta.recomendada}
                          </p>
                          <p>{t.puerta.opcionSedeDetalle}</p>
                          <Boton
                            onClick={() => {
                              setAcabaDeElegir(true);
                              void accion(() => api.elegirAutorizacion("APODERAMIENTO"));
                            }}
                            deshabilitado={ocupado || !hayConexion}
                          >
                            {estado === "NOT_STARTED" ? t.puerta.autorizarAhora : t.puerta.volverAEmpezar}
                          </Boton>
                        </div>
                        <div className="opcion-autorizacion opcion-desactivada">
                          <h3>{t.puerta.opcionCertificadoTitulo}</h3>
                          <p className="etiqueta-precio">{t.puerta.opcionCertificadoEstado}</p>
                          <p>{t.puerta.opcionCertificadoDetalle}</p>
                          <Boton variante="secundario" deshabilitado>
                            {t.autorizacion.masAdelante}
                          </Boton>
                        </div>
                      </div>
                    </>
                  ) : null}

                  {estado === "PENDING_SIGNATURE" ? (
                    <>
                      {/* Lo primero, la pregunta. Los pasos estan debajo,
                          plegados si vuelve de la Sede: ya los ha leido. */}
                      <div className="asistente-bloque">
                        <h3>{t.puerta.yaEstaTitulo}</h3>
                        <p>{t.puerta.yaEstaDetalle}</p>
                        <div className="acciones">
                          <Boton
                            onClick={() => void accion(() => api.declararAutorizacion())}
                            deshabilitado={ocupado || !hayConexion}
                          >
                            {t.autorizacion.yaLoHeHecho}
                          </Boton>
                          <a
                            className="enlace-como-boton"
                            href={t.autorizacion.enlaceSede}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t.autorizacion.enlaceSedeTexto}
                          </a>
                        </div>
                      </div>

                      <details className="puerta-ayuda" open={acabaDeElegir}>
                        <summary>{t.puerta.verPasos}</summary>
                        <ol className="pasos-autorizacion">
                          <li>{t.autorizacion.paso1}</li>
                          <li>{t.autorizacion.paso2}</li>
                          <li>{t.autorizacion.paso3(apoderado)}</li>
                          <li>{t.autorizacion.paso4}</li>
                        </ol>
                        {/* Copiar los datos: teclear mal un NIF es el fallo
                            mas comun del formulario de la Sede. */}
                        <div className="datos-apoderado">
                          <span>
                            <strong>{t.puerta.datosTitulo}</strong>
                            <small>{apoderado}</small>
                          </span>
                          <Boton variante="secundario" onClick={() => void copiarApoderado()}>
                            {copiado ? t.puerta.copiado : t.puerta.copiar}
                          </Boton>
                        </div>
                        <Aviso tono="info">{t.autorizacion.avisoPoder}</Aviso>
                        {/*
                          NUESTRA guia, en otra pestana: en la Sede hay que saber
                          QUE tramite marcar, y marcar el que no es deja un poder
                          que parece bueno y con el que todas las facturas
                          fallarian. Es un enlace de verdad: asi la pestana nueva
                          no la bloquea el navegador.
                        */}
                        <p>
                          <a href={t.puerta.rutaComoAutorizar} target="_blank" rel="noopener noreferrer">
                            {t.autorizacion.guiaTexto}
                          </a>
                        </p>
                      </details>

                      {/* El mayor reductor de friccion: que lo haga su gestoria. */}
                      <details className="puerta-ayuda">
                        <summary>{t.puerta.enviarTitulo}</summary>
                        <p>{t.puerta.enviarDetalle}</p>
                        {avisoCorreo !== null ? <Aviso tono="info">{avisoCorreo}</Aviso> : null}
                        <div className="acciones-en-linea">
                          <Boton
                            variante={destino === "GESTORIA" ? "principal" : "secundario"}
                            onClick={() => setDestino("GESTORIA")}
                          >
                            {t.puerta.destinoGestoria}
                          </Boton>
                          <Boton variante={destino === "YO" ? "principal" : "secundario"} onClick={() => setDestino("YO")}>
                            {t.puerta.destinoYo}
                          </Boton>
                        </div>
                        <Campo
                          id="correo-instrucciones"
                          etiqueta={t.puerta.correoEtiqueta}
                          ayuda={t.puerta.correoAyuda}
                          valor={correo}
                          onChange={setCorreo}
                          tipo="email"
                        />
                        <Boton
                          onClick={() => void enviarPasos()}
                          deshabilitado={ocupado || !hayConexion || correo.trim() === ""}
                        >
                          {ocupado ? t.puerta.enviando : t.puerta.enviar}
                        </Boton>
                      </details>
                    </>
                  ) : null}

                  {/* Ya no bloquea: declarado (se comprueba) o activo. */}
                  {!faltaAutorizacion && estado === "ACTIVE" ? (
                    <Aviso tono="exito" titulo={t.autorizacion.activaTitulo}>
                      {t.autorizacion.activa(new Date(datos.validadaEl ?? Date.now()).toLocaleDateString("es-ES"))}
                    </Aviso>
                  ) : null}
                  {!faltaAutorizacion && estado !== "ACTIVE" ? (
                    <Aviso tono="info" titulo={t.autorizacion.comprobandoTitulo}>
                      {t.autorizacion.comprobando}
                    </Aviso>
                  ) : null}

                  {/* No abre la puerta: sólo evita dejar tirado a quien no tiene Cl@ve. */}
                  {faltaAutorizacion ? (
                    <details className="puerta-ayuda">
                      <summary>{t.puerta.sinClaveTitulo}</summary>
                      <p>{t.puerta.sinClaveDetalle}</p>
                      <p>
                        <a href={t.puerta.sinClaveEnlace} target="_blank" rel="noopener noreferrer">
                          {t.puerta.sinClaveEnlaceTexto}
                        </a>
                      </p>
                    </details>
                  ) : null}

                  <div className="asistente-navegacion">
                    <Boton variante="secundario" onClick={() => irAPaso(1)}>
                      {t.puerta.atras}
                    </Boton>
                    {!faltaAutorizacion ? (
                      <Boton onClick={siguiente}>{t.puerta.continuar}</Boton>
                    ) : estado === "PENDING_SIGNATURE" && faltaMfa ? (
                      <Boton variante="terciario" onClick={() => irAPaso(3)}>
                        {t.puerta.autorizacionLuego}
                      </Boton>
                    ) : null}
                  </div>
                </>
              ) : null}

              {paso === 3 ? (
                <>
                  <h2 id="puerta-titulo" tabIndex={-1} ref={titulo}>
                    {t.puerta.mfaTitulo}
                  </h2>
                  <p className="asistente-entradilla">{t.puerta.mfaDetalle}</p>
                  {/*
                    El alta del TOTP, AQUI. Antes este paso mandaba a Seguridad
                    y cerraba la puerta: salir a mitad del recorrido para volver
                    despues es donde se queda la gente. Es el mismo componente
                    que en Seguridad, asi que el secreto y los codigos siguen
                    viviendo solo en su estado (ADR-0004).
                  */}
                  <PantallaSeguridad
                    enLinea
                    hayConexion={hayConexion}
                    onSesionCaducada={onSesionCaducada}
                    onActivada={() => {
                      onCambio();
                      void cargar();
                      // Con el segundo factor ya se puede confirmar: si falta,
                      // es lo siguiente (E-14), y si no, el final.
                      setPasoElegido(null);
                    }}
                  />
                  <div className="asistente-navegacion">
                    <Boton variante="secundario" onClick={() => irAPaso(2)}>
                      {t.puerta.atras}
                    </Boton>
                  </div>
                </>
              ) : null}
            </section>

            {/*
              La explicacion de cada paso, al lado en escritorio y debajo en el
              movil. Lo que antes no cabia: por que se pide, que hace falta
              tener a mano y, en el apoderamiento, por que no pedimos el
              certificado (OD-0039 D-04, contado para el cliente).
            */}
            <aside className="asistente-lateral">
              {paso === 1 ? (
                <>
                  <h3>{t.puerta.fiscalPorQueTitulo}</h3>
                  <ul>
                    {t.puerta.fiscalPorQue.map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                  <h3>{t.puerta.fiscalALaManoTitulo}</h3>
                  <ul>
                    {t.puerta.fiscalALaMano.map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                </>
              ) : null}

              {paso === 2 ? (
                <>
                  <h3>{t.puerta.queEsTitulo}</h3>
                  <p>{t.puerta.queEs}</p>
                  <h3>{t.puerta.certificadoTitulo}</h3>
                  <p>{t.puerta.certificadoEntradilla}</p>
                  <p>{t.puerta.certificadoRiesgo}</p>
                  <table className="asistente-comparativa">
                    <thead>
                      <tr>
                        <th scope="col">
                          <span className="solo-lector">{t.puerta.comparativaAspecto}</span>
                        </th>
                        <th scope="col">{t.puerta.comparativaCertificado}</th>
                        <th scope="col">{t.puerta.comparativaApoderamiento}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.puerta.comparativa.map((fila) => (
                        <tr key={fila.aspecto}>
                          <th scope="row">{fila.aspecto}</th>
                          <td>{fila.certificado}</td>
                          <td>{fila.apoderamiento}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p>{t.puerta.certificadoCierre}</p>
                </>
              ) : null}

              {paso === 3 ? (
                <>
                  <h3>{t.puerta.mfaPorQueTitulo}</h3>
                  <p>{t.puerta.mfaPorQue}</p>
                  <h3>{t.puerta.appTitulo}</h3>
                  <p>{t.puerta.appDetalle}</p>
                  <h3>{t.puerta.recuperacionTitulo}</h3>
                  <p>{t.puerta.recuperacion}</p>
                </>
              ) : null}
            </aside>
          </div>
        )}

        {/*
          Lo que se lee al final: que se puede cerrar y seguir, y que cerrar no
          deja nada hecho.
        */}
        {terminado ? null : <p className="puerta-salida">{t.puerta.cerrarAyuda}</p>}
      </div>
    </div>
  );
}
